import React, { useEffect } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { getMediaTypeFromUrl, getImageUrl } from '../../../utils/helpers';

const TikTokPreview = ({ 
  imageUrl, 
  caption,
  username = 'username',
  duration = 15
}) => {
  const { isRTL } = useLanguage();

  // Add CSS animations dynamically
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes tiktok-marquee {
        0% { transform: translateX(100%); }
        100% { transform: translateX(-100%); }
      }
      .tiktok-animate-marquee {
        animation: tiktok-marquee 10s linear infinite;
      }
      @keyframes tiktok-spin-slow {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      .tiktok-animate-spin-slow {
        animation: tiktok-spin-slow 3s linear infinite;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Get full image URL
  const fullImageUrl = imageUrl ? getImageUrl(imageUrl) : null;

  return (
    <div className="w-full max-w-[280px] mx-auto">
      <div className="relative bg-black rounded-3xl overflow-hidden shadow-2xl">
        {/* Phone Notch */}
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-6 bg-black rounded-full z-20"></div>
        
        {/* TikTok Content */}
        <div className="aspect-[9/16] relative">
          {/* Top Bar */}
          <div className="absolute top-8 left-0 right-0 z-10 px-4 flex items-center justify-between">
            <button className="text-white">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </button>
            <div className="flex gap-4">
              <span className="text-white font-semibold text-sm border-b-2 border-white pb-1">For You</span>
              <span className="text-white/60 font-medium text-sm pb-1">Following</span>
            </div>
            <button className="text-white">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </button>
          </div>

          {/* Video Content */}
          {fullImageUrl ? (
            getMediaTypeFromUrl(fullImageUrl) === 'video' ? (
              <video
                src={fullImageUrl}
                className="w-full h-full object-cover"
                controls
                muted
                playsInline
                loop
                preload="metadata"
              />
            ) : (
              <img 
                src={fullImageUrl} 
                alt="TikTok" 
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            )
          ) : (
            <div className="w-full h-full bg-gradient-to-b from-gray-900 via-gray-800 to-black flex items-center justify-center">
              <div className="text-center">
                <div className="relative">
                  <span className="text-7xl">🎵</span>
                  {/* Music Note Animation */}
                  <div className="absolute -right-4 top-0 animate-bounce">
                    <span className="text-2xl">♪</span>
                  </div>
                </div>
                <p className="text-white/50 text-sm mt-4">TikTok Preview</p>
                <p className="text-white/30 text-xs mt-1">{duration}s video</p>
              </div>
            </div>
          )}

          {/* Right Side Actions */}
          <div className="absolute right-3 bottom-32 z-10 flex flex-col items-center gap-5">
            {/* Profile */}
            <div className="relative">
              <div className="w-12 h-12 rounded-full bg-gray-400 border-2 border-white overflow-hidden">
                <div className="w-full h-full bg-gradient-to-br from-pink-400 to-purple-500"></div>
              </div>
              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            
            {/* Like */}
            <button className="flex flex-col items-center">
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                </svg>
              </div>
              <span className="text-white text-xs font-semibold">328.5K</span>
            </button>
            
            {/* Comment */}
            <button className="flex flex-col items-center">
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M21 6h-2v9H6v2c0 .55.45 1 1 1h11l4 4V7c0-.55-.45-1-1-1zm-4 6V3c0-.55-.45-1-1-1H3c-.55 0-1 .45-1 1v14l4-4h10c.55 0 1-.45 1-1z"/>
                </svg>
              </div>
              <span className="text-white text-xs font-semibold">2,456</span>
            </button>
            
            {/* Bookmark */}
            <button className="flex flex-col items-center">
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
              </div>
              <span className="text-white text-xs font-semibold">18.2K</span>
            </button>
            
            {/* Share */}
            <button className="flex flex-col items-center">
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
              </div>
              <span className="text-white text-xs font-semibold">Share</span>
            </button>
            
            {/* Music Disc */}
            <div className="w-10 h-10 rounded-full bg-gray-800 border-4 border-gray-700 tiktok-animate-spin-slow overflow-hidden">
              <div className="w-full h-full bg-gradient-to-br from-pink-500 via-purple-500 to-blue-500 flex items-center justify-center">
                <div className="w-3 h-3 rounded-full bg-gray-900"></div>
              </div>
            </div>
          </div>

          {/* Bottom Info */}
          <div className="absolute left-3 right-16 bottom-6 z-10">
            {/* Username */}
            <p className="text-white font-bold text-base mb-1">@{username}</p>
            
            {/* Caption */}
            <p className="text-white text-sm mb-2 line-clamp-2">
              {caption || 'Your caption will appear here... #fyp #viral #tiktok'}
            </p>
            
            {/* Music */}
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-white flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
              </svg>
              <div className="overflow-hidden flex-1 min-w-0">
                <p className="text-white text-xs whitespace-nowrap tiktok-animate-marquee">
                  Original sound - {username} ♪ Trending audio
                </p>
              </div>
            </div>
          </div>

          {/* Bottom Nav (simplified) */}
          <div className="absolute bottom-0 left-0 right-0 bg-black py-2 px-4 flex justify-around items-center">
            <button className="text-white text-center">
              <svg className="w-6 h-6 mx-auto" fill="currentColor" viewBox="0 0 24 24">
                <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
              </svg>
              <span className="text-xs">Home</span>
            </button>
            <button className="text-gray-500 text-center">
              <svg className="w-6 h-6 mx-auto" fill="currentColor" viewBox="0 0 24 24">
                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
              </svg>
              <span className="text-xs">Discover</span>
            </button>
            <button className="text-white">
              <div className="w-10 h-7 bg-white rounded-lg flex items-center justify-center relative overflow-hidden">
                <div className="absolute left-0 top-0 bottom-0 w-3 bg-cyan-400 rounded-l-lg"></div>
                <div className="absolute right-0 top-0 bottom-0 w-3 bg-red-500 rounded-r-lg"></div>
                <svg className="w-5 h-5 text-black relative z-10" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
              </div>
            </button>
            <button className="text-gray-500 text-center">
              <svg className="w-6 h-6 mx-auto" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
              </svg>
              <span className="text-xs">Inbox</span>
            </button>
            <button className="text-gray-500 text-center">
              <svg className="w-6 h-6 mx-auto" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
              </svg>
              <span className="text-xs">Profile</span>
            </button>
          </div>
        </div>
      </div>
      <p className="text-center text-xs text-gray-500 mt-2">TikTok Preview</p>
    </div>
  );
};

export default TikTokPreview;






